import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Flame, Search, Plus, ExternalLink, ArrowLeft, Send, Download, Activity,
  Building2, GraduationCap, MapPin, Clock, ThumbsUp, ThumbsDown, FileText,
  CalendarClock, TrendingUp, Zap, BarChart3, RefreshCw, ChevronRight, Loader2, Copy
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_MAP = {
  hot: { label: 'HET', icon: '🔥', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', border: 'border-red-300' },
  interesting: { label: 'INTRESSANT', icon: '🟠', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40', border: 'border-amber-300' },
  watch: { label: 'BEVAKA', icon: '🟡', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40', border: 'border-yellow-300' },
  low: { label: 'LÅG', icon: '⚪', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800', border: 'border-slate-200' },
};

const STATUS_LABELS = {
  new: 'Ny', reviewed: 'Granskad', find_contact: 'Kontakt ska hittas',
  ready_to_contact: 'Redo att kontakta', contacted: 'Kontaktad', responded: 'Svarat',
  meeting_booked: 'Möte bokat', quote: 'Offert', won: 'Vunnen',
  lost: 'Förlorad', not_relevant: 'Ej relevant', watch: 'Bevaka',
};

function ScoreBadge({ score, priority }) {
  const p = PRIORITY_MAP[priority] || PRIORITY_MAP.low;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-sm ${p.cls}`} data-testid="lead-score">
      <span>{p.icon}</span><span>{score}</span>
    </div>
  );
}

export default function LeadsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('leads');
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [logs, setLogs] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('lead_score');
  const [showNewLead, setShowNewLead] = useState(false);
  const [newLead, setNewLead] = useState({ organization: '', school: '', municipality: '', url: '', description: '', contact_name: '', contact_email: '', contact_title: '', notes: '' });
  const [analyzing, setAnalyzing] = useState(false);
  const [running, setRunning] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [showDraft, setShowDraft] = useState(false);
  const [activityText, setActivityText] = useState('');
  const [settings, setSettings] = useState(null);
  const [findingContacts, setFindingContacts] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadDashboard = useCallback(async () => {
    const r = await fetch(`${API}/api/leads/dashboard`, { headers: h });
    if (r.ok) setDashboard(await r.json());
  }, [token]);

  const loadLeads = useCallback(async () => {
    const params = new URLSearchParams({ sort_by: sortBy, limit: '100' });
    if (searchText) params.set('search', searchText);
    if (filterStatus !== 'all') params.set('status', filterStatus);
    if (filterPriority !== 'all') params.set('priority', filterPriority);
    const r = await fetch(`${API}/api/leads?${params}`, { headers: h });
    if (r.ok) { const d = await r.json(); setLeads(d.leads); setTotalLeads(d.total); }
  }, [token, searchText, filterStatus, filterPriority, sortBy]);

  const loadLogs = useCallback(async () => {
    const r = await fetch(`${API}/api/leads/search-logs`, { headers: h });
    if (r.ok) setLogs(await r.json());
  }, [token]);

  const loadLead = async (id) => {
    const r = await fetch(`${API}/api/leads/${id}`, { headers: h });
    if (r.ok) {
      const data = await r.json();
      setSelectedLead(data);
      // Auto-dismiss new signal badge when viewing
      if (data.has_new_signal) {
        fetch(`${API}/api/leads/${id}/dismiss-signal`, { method: 'PUT', headers: h });
      }
    }
  };

  useEffect(() => { loadDashboard(); loadLeads(); }, [loadDashboard, loadLeads]);
  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);
  useEffect(() => {
    if (tab === 'settings') {
      fetch(`${API}/api/leads/settings`, { headers: h }).then(r => r.ok ? r.json() : null).then(d => { if (d) setSettings(d); });
    }
  }, [tab]);

  const saveSettings = async () => {
    const r = await fetch(`${API}/api/leads/settings`, { method: 'PUT', headers: h, body: JSON.stringify(settings) });
    if (r.ok) toast.success('Inställningar sparade');
  };

  const findContacts = async (id) => {
    setFindingContacts(true);
    toast.info('Söker kontakter...');
    const r = await fetch(`${API}/api/leads/${id}/find-contacts`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      if (d.new_added > 0) toast.success(`${d.new_added} nya kontakter hittade`);
      else toast.info(d.contacts_found > 0 ? 'Kontakter redan tillagda' : 'Inga kontakter hittades — prova manuell sökning');
      loadLead(id);
    }
    setFindingContacts(false);
  };

  const runSearch = async () => {
    setRunning(true);
    toast.info('Leadsökning startar i bakgrunden...');
    try {
      const r = await fetch(`${API}/api/leads/run-search`, { method: 'POST', headers: h });
      if (r.ok) {
        toast.success('Sökning startad — resultat visas automatiskt');
        // Poll for completion
        _pollSearchStatus();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Kunde inte starta sökning');
        setRunning(false);
      }
    } catch (e) { toast.error('Nätverksfel'); setRunning(false); }
  };

  const _pollSearchStatus = () => {
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/leads/search-status`, { headers: h });
        if (r.ok) {
          const d = await r.json();
          if (!d.running) {
            clearInterval(interval);
            setRunning(false);
            toast.success('Leadsökning klar!');
            loadDashboard(); loadLeads(); loadLogs();
          }
        }
      } catch {}
    }, 5000); // Check every 5 sec
    // Safety timeout — stop polling after 6 min
    setTimeout(() => { clearInterval(interval); setRunning(false); loadDashboard(); loadLeads(); }, 360000);
  };

  const createLead = async () => {
    const r = await fetch(`${API}/api/leads`, { method: 'POST', headers: h, body: JSON.stringify(newLead) });
    if (r.ok) { toast.success('Lead skapad'); setShowNewLead(false); setNewLead({ organization: '', school: '', municipality: '', url: '', description: '', contact_name: '', contact_email: '', contact_title: '', notes: '' }); loadLeads(); loadDashboard(); }
  };

  const analyzeUrl = async () => {
    if (!newLead.url) return;
    setAnalyzing(true);
    try {
      const r = await fetch(`${API}/api/leads/analyze-url?url=${encodeURIComponent(newLead.url)}`, { method: 'POST', headers: h });
      if (r.ok) {
        const d = await r.json();
        setNewLead({ ...newLead, organization: d.organization || newLead.organization, school: d.school || newLead.school, municipality: d.municipality || newLead.municipality, description: d.problem_summary || newLead.description });
        toast.success(`Analys klar — Score: ${d.lead_score}, ${d.priority === 'hot' ? '🔥 HET' : d.priority}`);
      } else toast.error('Analys misslyckades');
    } catch { toast.error('Nätverksfel'); }
    setAnalyzing(false);
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/leads/${id}/status`, { method: 'PUT', headers: h, body: JSON.stringify({ status }) });
    if (selectedLead?.id === id) loadLead(id);
    loadLeads(); loadDashboard();
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Ta bort lead?')) return;
    await fetch(`${API}/api/leads/${id}`, { method: 'DELETE', headers: h });
    setSelectedLead(null); loadLeads(); loadDashboard();
  };

  const submitFeedback = async (id, isGood, reason = '') => {
    await fetch(`${API}/api/leads/${id}/feedback`, { method: 'POST', headers: h, body: JSON.stringify({ is_good: isGood, reason }) });
    toast.success(isGood ? 'Markerad som bra lead' : 'Markerad som dålig lead');
    if (selectedLead?.id === id) loadLead(id);
  };

  const generateDraft = async (id, contactName = '') => {
    const r = await fetch(`${API}/api/leads/${id}/email-draft?contact_name=${encodeURIComponent(contactName)}`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      setEmailDraft(d.draft);
      // Pre-fill email fields
      const lead = selectedLead;
      const contact = lead?.contacts?.find(c => c.email) || {};
      setEmailTo(contact.email || '');
      setEmailSubject(`Vapelarm — ${lead?.school || lead?.organization || lead?.municipality || ''}`);
      setShowDraft(true);
    }
  };

  const sendEmail = async (leadId) => {
    if (!emailTo || !emailDraft) { toast.error('Fyll i mottagare och mejltext'); return; }
    setSendingEmail(true);
    try {
      const r = await fetch(`${API}/api/leads/${leadId}/send-email`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ to_email: emailTo, subject: emailSubject, body: emailDraft }),
      });
      if (r.ok) {
        toast.success(`Mejl skickat till ${emailTo}`);
        setShowDraft(false);
        loadLead(leadId);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Kunde inte skicka mejl');
      }
    } catch { toast.error('Nätverksfel'); }
    setSendingEmail(false);
  };

  const addActivity = async (id) => {
    if (!activityText.trim()) return;
    await fetch(`${API}/api/leads/${id}/activity`, { method: 'POST', headers: h, body: JSON.stringify({ type: 'note', text: activityText }) });
    setActivityText(''); loadLead(id);
  };

  const convertToSales = async (id) => {
    const r = await fetch(`${API}/api/leads/${id}/convert-to-sales`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      if (d.already_existed) {
        toast.info('Lead finns redan i säljpipeline — navigerar dit');
      } else {
        toast.success('Konverterad till säljlead');
      }
      loadLead(id);
      // Navigate to sales portal
      navigate('/sales');
    }
  };

  const findSimilar = async (id) => {
    setRunning(true);
    toast.info('Söker liknande leads i bakgrunden...');
    try {
      const r = await fetch(`${API}/api/leads/find-similar?lead_id=${id}`, { method: 'POST', headers: h });
      if (r.ok) {
        toast.success('Sökning startad — resultat visas automatiskt');
        _pollSearchStatus();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Kunde inte starta sökning');
        setRunning(false);
      }
    } catch { toast.error('Nätverksfel'); setRunning(false); }
  };

  // ===== LEAD DETAIL VIEW =====
  if (selectedLead) {
    const lead = selectedLead;
    const p = PRIORITY_MAP[lead.priority] || PRIORITY_MAP.low;
    return (
      <div className="space-y-4" data-testid="lead-detail">
        <Button variant="ghost" size="sm" onClick={() => setSelectedLead(null)} className="gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Tillbaka</Button>
        {/* Header */}
        <div className={`rounded-xl border-2 ${p.border} p-5`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold">{lead.school || lead.organization || 'Okänd organisation'}</h2>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5"><MapPin className="h-3.5 w-3.5" />{lead.municipality || 'Okänd kommun'}</p>
              {lead.school_type && lead.school_type !== 'okänd' && <Badge variant="outline" className="mt-1 text-[10px]">{lead.school_type}</Badge>}
            </div>
            <div className="text-center"><ScoreBadge score={lead.lead_score} priority={lead.priority} /><div className="text-[10px] text-muted-foreground mt-1">{p.label}</div></div>
          </div>
        </div>

        {/* AI Summary */}
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Varför är detta en lead?</CardTitle></CardHeader>
          <CardContent><p className="text-sm">{lead.problem_summary}</p></CardContent>
        </Card>

        {/* Source Facts vs AI Assessment */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-700 dark:text-emerald-400">Källbelägg (SOURCE FACTS)</CardTitle></CardHeader>
            <CardContent>{lead.source_facts?.length > 0 ? <ul className="space-y-1.5">{lead.source_facts.map((f, i) => <li key={i} className="text-sm flex gap-2"><span className="text-emerald-500 mt-0.5">•</span><span>{f}</span></li>)}</ul> : <p className="text-xs text-muted-foreground">Inga verifierade fakta</p>}</CardContent>
          </Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm text-blue-700 dark:text-blue-400">AI-bedömning (INFERENCE)</CardTitle></CardHeader>
            <CardContent><p className="text-sm italic">{lead.ai_assessment || 'Ingen bedömning'}</p></CardContent>
          </Card>
        </div>

        {/* Score Components */}
        {lead.score_components && (
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Leadscore — {lead.lead_score}/100</CardTitle></CardHeader>
            <CardContent><div className="space-y-1.5">
              {[{ k: 'problem_specificity', l: 'Problem', max: 30 }, { k: 'school_relevance', l: 'Skolmiljö', max: 20 }, { k: 'purchase_signal', l: 'Köpsignal', max: 15 }, { k: 'freshness', l: 'Aktualitet', max: 15 }, { k: 'source_quality', l: 'Källkvalitet', max: 10 }, { k: 'contact_potential', l: 'Kontaktmöjlighet', max: 10 }].map(({ k, l, max }) => (
                <div key={k} className="flex items-center gap-2 text-xs">
                  <span className="w-28 text-muted-foreground">{l}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${((lead.score_components[k] || 0) / max) * 100}%` }} /></div>
                  <span className="font-bold w-12 text-right">{lead.score_components[k] || 0}/{max}</span>
                </div>
              ))}
              <div className="text-xs text-muted-foreground mt-2">{lead.score_explanation}</div>
            </div></CardContent>
          </Card>
        )}

        {/* Contacts */}
        {lead.contacts?.length > 0 && (
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Kontakter ({lead.contacts.length})</CardTitle></CardHeader>
            <CardContent>{lead.contacts.map((c, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b last:border-0">
                <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <div className="text-sm font-medium">{c.name || 'Okänd'}</div>
                  <div className="text-xs text-muted-foreground">{c.title}</div>
                  {c.email && <div className="text-xs text-blue-600">{c.email}</div>}
                  {c.phone && <div className="text-xs">{c.phone}</div>}
                  {c.confidence > 0 && <Badge variant="outline" className="text-[9px] mt-0.5">Konfidens: {Math.round(c.confidence * 100)}%</Badge>}
                </div>
              </div>
            ))}</CardContent>
          </Card>
        )}

        {/* Sources */}
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Källor ({lead.sources?.length || 0})</CardTitle></CardHeader>
          <CardContent>{lead.sources?.map(s => (
            <div key={s.id} className="flex items-start gap-3 py-2 border-b last:border-0">
              <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1">{s.title || s.url}<ExternalLink className="h-3 w-3" /></a>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{s.excerpt}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                  <span>{s.content_type?.toUpperCase()}</span>
                  {s.published_at && <span>Publicerad: {s.published_at}</span>}
                  <span>Hittad: {s.discovered_at?.slice(0, 10)}</span>
                </div>
              </div>
            </div>
          ))}</CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Select value={lead.status} onValueChange={v => updateStatus(lead.id, v)}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => generateDraft(lead.id, lead.contacts?.[0]?.name)} data-testid="generate-draft-btn"><Send className="h-3.5 w-3.5 mr-1" /> Skapa mejlutkast</Button>
          <Button size="sm" variant="outline" onClick={() => findContacts(lead.id)} disabled={findingContacts}>{findingContacts ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />} Hitta kontakter</Button>
          <Button size="sm" variant="outline" onClick={() => findSimilar(lead.id)} disabled={running}>{running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1" />} Hitta liknande</Button>
          <Button size="sm" variant="outline" onClick={() => convertToSales(lead.id)}><FileText className="h-3.5 w-3.5 mr-1" /> Skapa offert</Button>
          <Button size="sm" variant="outline" onClick={() => submitFeedback(lead.id, true)}><ThumbsUp className="h-3.5 w-3.5 mr-1" /> Bra lead</Button>
          <Button size="sm" variant="outline" onClick={() => submitFeedback(lead.id, false, 'Ej relevant')}><ThumbsDown className="h-3.5 w-3.5 mr-1" /> Dålig lead</Button>
          <Button size="sm" variant="destructive" onClick={() => deleteLead(lead.id)}>Ta bort</Button>
        </div>

        {/* Activities */}
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Aktiviteter</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3"><Input placeholder="Lägg till anteckning..." value={activityText} onChange={e => setActivityText(e.target.value)} className="text-sm" /><Button size="sm" onClick={() => addActivity(lead.id)} disabled={!activityText.trim()}>Spara</Button></div>
            <div className="space-y-2">{(lead.activities || []).slice().reverse().map((a, i) => (
              <div key={i} className="flex gap-2 text-xs"><span className="text-muted-foreground w-28 flex-shrink-0">{a.timestamp?.slice(0, 16).replace('T', ' ')}</span><span>{a.text}</span></div>
            ))}</div>
          </CardContent>
        </Card>

        {/* Email Draft Dialog */}
        <Dialog open={showDraft} onOpenChange={setShowDraft}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Mejlutkast</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Till</label>
                <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="mejl@kommun.se" data-testid="email-to" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Ämne</label>
                <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} data-testid="email-subject" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Meddelande</label>
                <Textarea value={emailDraft} onChange={e => setEmailDraft(e.target.value)} rows={12} className="text-sm" data-testid="email-body" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(emailDraft).catch(() => {}); toast.success('Kopierat!'); }}><Copy className="h-3.5 w-3.5 mr-1" /> Kopiera</Button>
              <Button variant="outline" size="sm" onClick={() => setShowDraft(false)}>Stäng</Button>
              <Button size="sm" onClick={() => sendEmail(lead.id)} disabled={sendingEmail || !emailTo} className="bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="send-email-btn">
                {sendingEmail ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Skicka
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ===== MAIN LIST VIEW =====
  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center"><Flame className="h-5 w-5 text-white" /></div>
            Vapelarm Leads
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 ml-[46px]">Automatisk leadmotor för svenska skolor</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowNewLead(true)} data-testid="new-lead-btn"><Plus className="h-4 w-4 mr-1" /> Ny lead</Button>
          <Button onClick={runSearch} disabled={running} className="bg-gradient-to-r from-red-600 to-orange-600" data-testid="run-search-btn">
            {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />} Kör leadsökning
          </Button>
        </div>
      </div>

      {/* KPI */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { v: dashboard.new_today, l: 'Nya idag', icon: Zap, c: 'from-blue-50 to-white dark:from-blue-950' },
            { v: dashboard.hot, l: 'Heta leads', icon: Flame, c: 'from-red-50 to-white dark:from-red-950' },
            { v: dashboard.status_counts?.new || 0, l: 'Ej kontaktade', icon: Search, c: 'from-amber-50 to-white dark:from-amber-950' },
            { v: dashboard.status_counts?.meeting_booked || 0, l: 'Möten', icon: CalendarClock, c: 'from-violet-50 to-white dark:from-violet-950' },
            { v: dashboard.status_counts?.won || 0, l: 'Vunna', icon: TrendingUp, c: 'from-emerald-50 to-white dark:from-emerald-950' },
          ].map(({ v, l, icon: Icon, c }) => (
            <div key={l} className={`rounded-xl border bg-gradient-to-br ${c} p-4 relative overflow-hidden`}>
              <Icon className="absolute -right-2 -top-2 h-16 w-16 opacity-[0.06]" strokeWidth={1.5} />
              <div className="text-3xl font-extrabold">{v}</div><div className="text-xs text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Follow-ups & New Signals */}
      {dashboard && (dashboard.follow_up_leads?.length > 0 || dashboard.new_signal_leads?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-3">
          {dashboard.follow_up_leads?.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-amber-500" /> Uppföljning idag ({dashboard.follow_up_leads.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {dashboard.follow_up_leads.map(l => (
                  <div key={l.id} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-muted/50 rounded-lg px-2 -mx-2" onClick={() => loadLead(l.id)}>
                    <ScoreBadge score={l.lead_score} priority={l.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{l.school || l.organization || '?'}</div>
                      <div className="text-[10px] text-muted-foreground">{l.municipality} — {l.follow_up_date}</div>
                    </div>
                    <Badge variant="outline" className="text-[9px]">{STATUS_LABELS[l.status] || l.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {dashboard.new_signal_leads?.length > 0 && (
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><Zap className="h-4 w-4 text-blue-500" /> Nya signaler ({dashboard.new_signal_leads.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {dashboard.new_signal_leads.map(l => (
                  <div key={l.id} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-muted/50 rounded-lg px-2 -mx-2" onClick={() => loadLead(l.id)}>
                    <ScoreBadge score={l.lead_score} priority={l.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{l.school || l.organization || '?'}</div>
                      <div className="text-[10px] text-muted-foreground">{l.municipality} — senaste: {l.last_signal_at?.slice(0, 10)}</div>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 text-[9px]">NY SIGNAL</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="leads" className="gap-1.5"><Search className="h-3.5 w-3.5" /> Leads ({totalLeads})</TabsTrigger>
          <TabsTrigger value="logs" className="gap-1.5"><Activity className="h-3.5 w-3.5" /> Körlogg</TabsTrigger>
          <TabsTrigger value="settings" className="gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> Inställningar</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-4 space-y-3">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <Input placeholder="Sök organisation, skola, kommun..." value={searchText} onChange={e => setSearchText(e.target.value)} className="w-64" data-testid="leads-search" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alla statusar</SelectItem>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Prioritet" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Alla prioriteter</SelectItem>{Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.icon} {v.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="lead_score">Högst score</SelectItem><SelectItem value="newest">Nyast</SelectItem><SelectItem value="updated">Senast uppdaterad</SelectItem></SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={loadLeads}><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>

          {/* Lead cards */}
          {leads.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Flame className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Inga leads ännu</p>
              <p className="text-xs mt-1">Klicka "Kör leadsökning" för att hitta leads automatiskt</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leads.map(lead => {
                const p = PRIORITY_MAP[lead.priority] || PRIORITY_MAP.low;
                return (
                  <div key={lead.id} className={`group rounded-xl border ${p.border} bg-card p-4 hover:shadow-md transition-all cursor-pointer`} onClick={() => loadLead(lead.id)} data-testid={`lead-card-${lead.id}`}>
                    <div className="flex items-start gap-4">
                      <ScoreBadge score={lead.lead_score} priority={lead.priority} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{lead.school || lead.organization || 'Okänd'}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.municipality || '—'}</div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{lead.problem_summary}</p>
                      </div>
                      <div className="text-right text-[10px] text-muted-foreground flex-shrink-0 space-y-0.5">
                        <div>{lead.discovered_at?.slice(0, 10)}</div>
                        <div>Källor: {lead.source_count || 0}</div>
                        <Badge variant="outline" className="text-[9px]">{STATUS_LABELS[lead.status] || lead.status}</Badge>
                        {lead.has_new_signal && <Badge className="bg-blue-100 text-blue-700 text-[9px]">NY SIGNAL</Badge>}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Logs */}
        <TabsContent value="logs" className="mt-4 space-y-3">
          {logs.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">Inga körloggar ännu</p> : logs.map((log, i) => (
            <Card key={i}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{log.started_at?.slice(0, 16).replace('T', ' ')}</span>
                  <span className="text-muted-foreground">{Math.round(log.duration_seconds || 0)}s</span>
                </div>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mt-2 text-xs">
                  {[['Sökningar', log.queries], ['Resultat', log.search_results], ['Hämtade', log.pages_fetched], ['Analyserade', log.analyzed], ['Relevanta', log.relevant], ['Nya leads', log.new_leads], ['Heta', log.hot_leads]].map(([l, v]) => (
                    <div key={l}><span className="text-muted-foreground">{l}:</span> <strong>{v}</strong></div>
                  ))}
                </div>
                {log.errors?.length > 0 && <div className="mt-2 text-xs text-red-500">{log.errors.length} fel</div>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="mt-4">
          {settings && (
            <Card>
              <CardHeader><CardTitle className="text-base">Lead Engine-inställningar</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium w-48">Lead Engine</label>
                  <Select value={settings.enabled ? 'on' : 'off'} onValueChange={v => setSettings({ ...settings, enabled: v === 'on' })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="on">PÅ</SelectItem><SelectItem value="off">AV</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium w-48">Körfrekvens (timmar)</label>
                  <Input type="number" className="w-32" value={settings.run_frequency_hours} onChange={e => setSettings({ ...settings, run_frequency_hours: parseInt(e.target.value) || 24 })} />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium w-48">Min score att spara</label>
                  <Input type="number" className="w-32" min={0} max={100} value={settings.min_score_to_save} onChange={e => setSettings({ ...settings, min_score_to_save: parseInt(e.target.value) || 40 })} />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium w-48">Het lead-gräns</label>
                  <Input type="number" className="w-32" min={0} max={100} value={settings.hot_threshold} onChange={e => setSettings({ ...settings, hot_threshold: parseInt(e.target.value) || 80 })} />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium w-48">Notifikationsgräns</label>
                  <Input type="number" className="w-32" min={0} max={100} value={settings.notification_threshold} onChange={e => setSettings({ ...settings, notification_threshold: parseInt(e.target.value) || 85 })} />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium w-48">Max resultat per sökfras</label>
                  <Input type="number" className="w-32" min={1} max={20} value={settings.max_results_per_query} onChange={e => setSettings({ ...settings, max_results_per_query: parseInt(e.target.value) || 5 })} />
                </div>
                <Button onClick={saveSettings} className="mt-2">Spara inställningar</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* New Lead Dialog */}
      <Dialog open={showNewLead} onOpenChange={setShowNewLead}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Ny lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2"><Input placeholder="URL (valfritt)" value={newLead.url} onChange={e => setNewLead({ ...newLead, url: e.target.value })} /><Button variant="outline" size="sm" onClick={analyzeUrl} disabled={!newLead.url || analyzing}>{analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}</Button></div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Organisation" value={newLead.organization} onChange={e => setNewLead({ ...newLead, organization: e.target.value })} />
              <Input placeholder="Skola" value={newLead.school} onChange={e => setNewLead({ ...newLead, school: e.target.value })} />
            </div>
            <Input placeholder="Kommun" value={newLead.municipality} onChange={e => setNewLead({ ...newLead, municipality: e.target.value })} />
            <Textarea placeholder="Beskrivning / problem" value={newLead.description} onChange={e => setNewLead({ ...newLead, description: e.target.value })} rows={2} />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Kontaktnamn" value={newLead.contact_name} onChange={e => setNewLead({ ...newLead, contact_name: e.target.value })} />
              <Input placeholder="E-post" value={newLead.contact_email} onChange={e => setNewLead({ ...newLead, contact_email: e.target.value })} />
              <Input placeholder="Titel" value={newLead.contact_title} onChange={e => setNewLead({ ...newLead, contact_title: e.target.value })} />
            </div>
            <Textarea placeholder="Anteckningar" value={newLead.notes} onChange={e => setNewLead({ ...newLead, notes: e.target.value })} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLead(false)}>Avbryt</Button>
            <Button onClick={createLead} disabled={!newLead.organization && !newLead.school && !newLead.municipality}>Skapa lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
